import { STORY_AUDIO_CORRECTED_QUERY } from '@src/gql/story-gql';
import { useDatoQuerySubscription } from '@src/hooks/useDatoQuerySubscription';
import {
  fetchCorrectedTranscription,
  isCorrectedTranscription,
} from '@src/services/audio/CorrectedAudioSwapService';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { LogManager } from '@src/utility/Logger';
import { runInAction, when } from 'mobx';
import { useEffect, useRef } from 'react';

const logger = LogManager.getLogger('hook:CorrectedAudioHotSwap');

interface StoryAudioCorrectedResult {
  story: {
    id: string;
    uploadLog?: {
      audioCorrected?: { status?: string } | null;
    } | null;
  } | null;
}

/**
 * PLAT-11226 — corrected-audio hot-swap. Mounted once at the Creator Studio
 * root. Watches the story's `audio_corrected` step-log signal (written when
 * BOTH audio-only-corrected.mp3 and waveformdata-corrected.json exist), then:
 *
 *   1. Defers while the user is playing/scrubbing — the swap applies at the
 *      next pause/idle, never mid-playback. Playhead and selection are
 *      untouched (raw↔corrected timings are identical per the
 *      timing-equivalence research; only URLs change).
 *   2. Re-populates the transcription fields — populate prefers `-corrected`
 *      keys, so this returns freshly-signed corrected URLs.
 *   3. Applies audio-only URL + transcription fields and reloads the
 *      waveform (denoising visibly moves silence/speech boundaries).
 *   4. Passive toast: "Audio enhanced". No opt-in fork.
 *
 * Renditions need no client action here — they are inaudible in the editor
 * and resolve via populate preference on the next fetch. Stories without
 * enhancement never fire the signal; stories opened after correction load
 * corrected URLs from the start (guarded below).
 */
export function useCorrectedAudioHotSwap(): void {
  const videoCreator = useVideoCreatorStore();
  const storyId = videoCreator.storyId;
  const swappedRef = useRef(false);

  // Subscribed unconditionally — with no storyId the filter matches nothing
  // and `story` stays null, which reads as "signal not fired".
  const { data, error } = useDatoQuerySubscription<
    StoryAudioCorrectedResult,
    { id: string }
  >({
    query: STORY_AUDIO_CORRECTED_QUERY,
    variables: { id: storyId ?? '' },
  });

  useEffect(() => {
    // Field missing in this Dato environment (deploy-order gap) or transport
    // trouble — the swap simply doesn't happen; nothing else depends on this.
    if (error) {
      logger.warn('audio_corrected subscription error — hot-swap inactive', {
        error,
      });
    }
  }, [error]);

  const signalFired =
    data?.story?.uploadLog?.audioCorrected?.status === 'success';

  useEffect(() => {
    if (!signalFired || !storyId || swappedRef.current) return;
    if (isCorrectedTranscription(videoCreator.story?.transcription)) {
      // Loaded corrected from the start — nothing to swap.
      swappedRef.current = true;
      return;
    }
    swappedRef.current = true;

    let cancelled = false;
    const disposeIdleWait = when(
      () => !videoCreator.isPlaying && !videoCreator.isScrubbing,
      async () => {
        if (cancelled) return;
        const result = await fetchCorrectedTranscription(storyId);
        if (cancelled) return;
        if (!result.ok) {
          // Not visible yet — allow a retry on the next subscription tick.
          logger.warn('corrected swap skipped', { reason: result.error });
          swappedRef.current = false;
          return;
        }
        runInAction(() => {
          const story = videoCreator.story;
          if (!story) return;
          story.transcription = {
            ...story.transcription,
            ...result.value,
          };
          videoCreator.originalVideoAudioUrl = result.value.audio?.url;
        });
        // Reload the waveform from the corrected data (load-once otherwise).
        await videoCreator.loadOriginalWaveformData();
        videoCreator.showToast({ state: 'success', message: 'Audio enhanced' });
        logger.info('corrected audio swapped in', { storyId });
      },
    );

    return () => {
      cancelled = true;
      disposeIdleWait();
    };
  }, [signalFired, storyId, videoCreator]);
}
